// @ts-check
import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import _ from 'lodash';
import uid from 'uid';
import api from 'utils/api';
import { Field } from 'utils/reduxFormWrapper';
import Icon from 'shared/components/Icon';
import Spinner from 'shared/components/Spinner';
import styles from './SearchingTextInput.module.scss';

const fetchResults = searchTerm =>
  api.get('/agent_portal/api/patient_members', { params: { string: searchTerm } });

const SearchResults = ({ searchResults, pathForResult }) => (
  <div>
    {searchResults.map(result => (
      <div key={result.id}>
        <Link
          className={styles.searching_input_result}
          to={pathForResult({ id: result.patientId })}
        >
          <div className={styles.result_name}>{result.commaDelimitedName}</div>
          <div>
            <span className={styles.result_subfield_label}>DOB:</span>
            <span>{result.dob}</span>
            <span className={styles.result_subfield_label}>Phone:</span>
            <span>{result.formattedPrimaryPhone}</span>
          </div>
        </Link>
      </div>
    ))}
  </div>
);

const NoResults = () => (
  <div style={{ padding: 12 }}>
    <span>No search results</span>
  </div>
);

const SearchingSpinnerCover = () => (
  <div className={styles.searching_input_results_spinner_cover}>
    <div className={styles.center_spinner_container}>
      <Spinner />
    </div>
  </div>
);

/** @typedef {{
  onFocus: () => any,
  onBlur: () => any,
  pathForResult: string
}} SearchingTextInputProps
*/
/** @typedef {{
  hasSearched: boolean,
  focused: boolean,
  searching: boolean,
  searchRequestId: (string|null),
  searchResults: Array
}} SearchingTextInputState
*/

/** @augments { Component<SearchingTextInputProps, SearchingTextInputState> } */
class SearchingTextInput extends Component {
  constructor() {
    super();
    this.state = {
      hasSearched: false,
      focused: false,
      searching: false,
      searchRequestId: null,
      searchResults: [],
    };
    this.handleWindowClick = this.handleWindowClick.bind(this);
    this.focus = this.focus.bind(this);
    this.unFocus = this.unFocus.bind(this);
    this.search = _.debounce(this.search, 150).bind(this);
    this.tabOut = this.tabOut.bind(this);
  }

  componentDidMount() {
    window.addEventListener('click', this.handleWindowClick);
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.handleWindowClick);
  }

  /**
   * On windowClick, checks to see if the click was outside this element,
   * and if it was, hides the search menu.
   *
   * @param {MessageEvent} event
   */
  handleWindowClick(event) {
    if (this.state.focused) {
      const element = findDOMNode(this);
      if (event.target !== element && !element.contains(event.target) && this.state.focused) {
        this.unFocus();
      }
    }
  }

  /**
   * Pop open the search menu
   */
  focus() {
    if (this.props.onFocus) {
      this.props.onFocus();
    }
    this.setState({ focused: true });
  }

  /**
   * Close the search menu
   */
  unFocus() {
    this.setState({ focused: false });
  }

  /**
   * This allows the user to "tab out" of the field, so that they can still fill in the
   * form without touching the mouse. It's a handler for the `keyDown` event on the input.
   *
   * @param {React.SyntheticEvent} e
   */
  tabOut(e) {
    if (e.key === 'Tab') {
      this.unFocus();
    }
  }

  /**
   * Search for the given value. Manages state changes around the search request.
   *
   * @param {React.SyntheticEvent} e The `onChange` event object provided by redux-form
   * @param {string} newValue The new value the input holds
   */
  search(e, newValue) {
    // TODO: Take into account "external" search params (i.e. other fields)
    const searchRequestId = uid('8');
    this.setState(() => ({ searching: true, searchRequestId }));

    return fetchResults(newValue).then(({ data }) => {
      this.setState(prevState => {
        return prevState.searchRequestId === searchRequestId
          ? { hasSearched: true, searching: false, searchResults: data.patientMembers }
          : {};
      });
    });
  }

  render() {
    const { pathForResult, onBlur } = this.props;
    return (
      <div style={{ position: 'relative', height: 30 }}>
        <Field
          key="lastNameInput"
          name="lastName"
          component="input"
          type="text"
          placeholder="Patient Last Name"
          onFocus={this.focus}
          onBlur={onBlur}
          onChange={this.search}
          style={{ position: 'absolute', zIndex: 1, width: 240 }}
          autoComplete="off"
          onKeyDown={this.tabOut}
        />
        {this.state.focused ? (
          <div className={styles.searching_input_modal}>
            <div className={styles.searching_input_header}>
              <div className={styles.searching_input_close_icon_container} onClick={this.unFocus}>
                <Icon name="x-circle" />
              </div>
              <span className={styles.searching_input_title}>Type to search</span>
            </div>
            <div className={styles.searching_input_content}>
              <div className={styles.searching_input_behind_input}>
                {/* This is just a space-filler _behind_ the actual input */}
              </div>
              {this.state.hasSearched ? (
                <div className={styles.searching_input_list}>
                  {this.state.searching ? <SearchingSpinnerCover /> : null}
                  {this.state.searchResults.length > 0 ? (
                    <SearchResults
                      searchResults={this.state.searchResults}
                      pathForResult={pathForResult}
                    />
                  ) : (
                    <NoResults />
                  )}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}

SearchingTextInput.propTypes = {
  pathForResult: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

export default SearchingTextInput;
